import { Component, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CatalogDetailBreadcrumbService } from '@/app/layout/catalog-detail-breadcrumb.service';
import { WorkflowDetailBreadcrumbService } from '@/app/layout/workflow-detail-breadcrumb.service';
import { RequestDetailBreadcrumbService } from '@/app/layout/request-detail-breadcrumb.service';
import { SourceDetailBreadcrumbService } from '@/app/layout/source-detail-breadcrumb.service';
import { TreatmentProtocolDetailBreadcrumbService } from '@/app/layout/treatment-protocol-detail-breadcrumb.service';
import { WorkflowRequestBreadcrumbService } from '@/app/layout/workflow-request-breadcrumb.service';
import { V2BreadcrumbService } from '@/app/layout/v2-breadcrumb.service';
import { AdaptersService } from '@/app/pages/adapters/adapters.service';
import { AuthService } from '@/app/auth/auth.service';
import { isAdminPath } from '@/app/pages/admin/admin-nav';

interface Breadcrumb {
    label: string;
    url?: string;
}

@Component({
    selector: '[app-breadcrumb]',
    standalone: true,
    imports: [CommonModule, RouterModule, ButtonModule, RippleModule, TooltipModule],
    template: `
        <nav class="layout-breadcrumb">
            <ol>
                <li><i class="pi pi-home"></i></li>
                <ng-template ngFor let-item let-last="last" [ngForOf]="breadcrumbs$ | async">
                    <li><i class="pi pi-angle-right"></i></li>
                    <li>
                        @if (item.url && !last) {
                            <a [routerLink]="item.url" class="layout-breadcrumb-link">{{ item.label }}</a>
                        } @else {
                            <span>{{ item.label }}</span>
                        }
                    </li>
                </ng-template>
            </ol>
        </nav>
        <div class="layout-breadcrumb-buttons">
            <button pButton pRipple type="button" icon="pi pi-cloud-upload" class="p-button-rounded p-button-text p-button-plain"></button>
            <button pButton pRipple type="button" icon="pi pi-bookmark" class="p-button-rounded p-button-text p-button-plain"></button>
            <button
                pButton
                pRipple
                type="button"
                icon="pi pi-power-off"
                class="p-button-rounded p-button-text p-button-plain"
                pTooltip="Logout"
                tooltipPosition="bottom"
                (click)="logout()"
            ></button>
        </div>
    `,
    host: {
        class: 'layout-breadcrumb-container'
    }
})
export class AppBreadcrumb {
    private readonly router = inject(Router);
    private readonly auth = inject(AuthService);
    private readonly catalogDetailLabel = inject(CatalogDetailBreadcrumbService);
    private readonly workflowDetailLabel = inject(WorkflowDetailBreadcrumbService);
    private readonly requestDetailLabel = inject(RequestDetailBreadcrumbService);
    private readonly sourceDetailLabel = inject(SourceDetailBreadcrumbService);
    private readonly tpDetailLabel = inject(TreatmentProtocolDetailBreadcrumbService);
    private readonly workflowRequestLabel = inject(WorkflowRequestBreadcrumbService);
    private readonly v2Crumb = inject(V2BreadcrumbService);
    private readonly adaptersSvc = inject(AdaptersService);

    private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();


    constructor() {
        merge(
            this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
            this.catalogDetailLabel.label$,
            this.workflowDetailLabel.label$,
            this.workflowDetailLabel.journey$,
            this.requestDetailLabel.label$,
            this.sourceDetailLabel.label$,
            this.tpDetailLabel.label$,
            this.workflowRequestLabel.ctx$,
            this.v2Crumb.regimen$,
            this.v2Crumb.medication$,
            this.v2Crumb.orderSet$
        ).subscribe(() => {
            this.rebuildBreadcrumbs();
        });
    }

    private rebuildBreadcrumbs(): void {
        const path = this.router.url.split('?')[0].replace(/\/$/, '') || '/';

        // CB-148 — v2 regimen/medication/order-set breadcrumbs (Regimens / <name> [...]).
        // v2 list roots first: "Regimens / New experience" and "Order sets / New experience",
        // matching the detail trails below (and replacing the in-page crumb the list screens drew).
        if (/^\/v2\/regimens$/.exec(path)) {
            this.emit(path, [
                { label: 'Regimens', url: '/regimens' },
                { label: 'New experience', url: path }
            ]);
            return;
        }
        if (/^\/v2\/order-sets$/.exec(path)) {
            this.emit(path, [
                { label: 'Order sets', url: '/order-sets' },
                { label: 'New experience', url: path }
            ]);
            return;
        }
        const v2Med = /^\/v2\/(regimens|preview)\/([^/]+)\/medications\/([^/]+)$/.exec(path);
        if (v2Med) {
            const base = `/v2/${v2Med[1]}/${v2Med[2]}`;
            this.emit(path, [
                { label: 'Regimens', url: '/regimens' },
                { label: this.v2Crumb.getRegimen() ?? v2Med[2], url: base },
                { label: 'Medications', url: `${base}/medications` },
                { label: this.v2Crumb.getMedication() ?? 'Medication', url: path }
            ]);
            return;
        }
        const v2Reg = /^\/v2\/(regimens|preview)\/([^/]+)(?:\/(?:overview|schedule|walkthrough|medications|clinical-reference|rules|review))?$/.exec(path);
        if (v2Reg) {
            this.emit(path, [
                { label: 'Regimens', url: '/regimens' },
                { label: this.v2Crumb.getRegimen() ?? v2Reg[2], url: `/v2/${v2Reg[1]}/${v2Reg[2]}` }
            ]);
            return;
        }
        // Matches both the v2 order-set page (singular /v2/order-set/:id) and the editor (plural /v2/order-sets/:id).
        const v2OrderSet = /^\/v2\/(?:preview\/[^/]+\/)?order-sets?\/([^/]+)$/.exec(path);
        if (v2OrderSet) {
            const os = this.v2Crumb.getOrderSet();
            if (os?.regimenPath) {
                this.emit(path, [
                    { label: 'Regimens', url: '/regimens' },
                    { label: os.regimenName ?? 'Regimen', url: os.regimenPath },
                    { label: 'Order sets', url: '/order-sets' },
                    { label: os.label ?? v2OrderSet[1], url: path }
                ]);
            } else {
                this.emit(path, [
                    { label: 'Order sets', url: '/order-sets' },
                    { label: os?.label ?? v2OrderSet[1], url: path }
                ]);
            }
            return;
        }

        // The proposed-structure page (Order list / By phase / EMR views) shares the v2 order-set trail:
        // Regimens / <regimen> / Order sets / <order set>. The component feeds the labels via V2BreadcrumbService.
        const proposedStructure = /^\/order-sets\/([^/]+)\/proposed-structure$/.exec(path);
        if (proposedStructure) {
            const os = this.v2Crumb.getOrderSet();
            if (os?.regimenPath) {
                this.emit(path, [
                    { label: 'Regimens', url: '/regimens' },
                    { label: os.regimenName ?? 'Regimen', url: os.regimenPath },
                    { label: 'Order sets', url: '/order-sets' },
                    { label: os.label ?? proposedStructure[1], url: path }
                ]);
            } else {
                this.emit(path, [
                    { label: 'Order sets', url: '/order-sets' },
                    { label: os?.label ?? proposedStructure[1], url: path }
                ]);
            }
            return;
        }

        const regimenDetail = /^\/regimens\/([^/]+)$/.exec(path);
        if (regimenDetail) {
            const id = regimenDetail[1];
            const regimen = this.catalogDetailLabel.getRegimenLabel();
            this.emit(path, [
                { label: 'Regimens', url: '/regimens' },
                { label: regimen ?? id, url: path }
            ]);
            return;
        }

        // Clinical Validation Workspace — same "Regimens >> {name}" trail as regimen detail.
        const clinicalValidation = /^\/clinical-validation\/([^/]+)$/.exec(path);
        if (clinicalValidation) {
            const regimen = this.catalogDetailLabel.getRegimenLabel();
            this.emit(path, [
                { label: 'Regimens', url: '/regimens' },
                { label: regimen ?? 'Clinical Validation', url: path }
            ]);
            return;
        }

        if (/^\/adapters\/schema$/.exec(path)) {
            this.emit(path, [
                { label: 'Adapters', url: '/adapters' },
                { label: 'CBCM Schema', url: path }
            ]);
            return;
        }

        const adapterEdit = /^\/adapters\/([^/]+)\/edit$/.exec(path);
        if (adapterEdit) {
            const id = adapterEdit[1];
            const name = this.adaptersSvc.getAdapterNameSnapshot(id);
            this.emit(path, [
                { label: 'Adapters', url: '/adapters' },
                { label: name ?? id },
                { label: 'Transformation Studio' }
            ]);
            return;
        }

        const adapterHistory = /^\/adapters\/([^/]+)\/history$/.exec(path);
        if (adapterHistory) {
            const id = adapterHistory[1];
            const name = this.adaptersSvc.getAdapterNameSnapshot(id);
            this.emit(path, [
                { label: 'Adapters', url: '/adapters' },
                { label: name ?? id, url: `/adapters/${id}/edit` },
                { label: 'Version history' }
            ]);
            return;
        }

        const workflowDetail = /^\/workflows-old\/([^/]+)(\/activity)?$/.exec(path);
        if (workflowDetail) {
            const id = workflowDetail[1];
            const name = this.workflowDetailLabel.getWorkflowLabel();
            const isActivity = !!workflowDetail[2];
            const detailCrumbs: Breadcrumb[] = [
                { label: 'Workflows (legacy)', url: '/workflows-old' },
                { label: name ?? id, url: `/workflows-old/${id}` }
            ];
            if (isActivity) {
                detailCrumbs.push({ label: 'Active protocols' });
            }
            this.emit(path, detailCrumbs);
            return;
        }

        const wfDetail = /^\/workflows\/([^/]+)$/.exec(path);
        if (wfDetail) {
            this.emit(path, [
                { label: 'Workflows', url: '/workflows' },
                { label: this.workflowDetailLabel.getWorkflowLabel() ?? 'Workflow', url: path }
            ]);
            return;
        }

        if (path === '/journey-templates') {
            const journey = this.workflowDetailLabel.getJourneyLabel();
            this.emit(path, [{ label: 'Journey templates', url: path }, ...(journey ? [{ label: journey }] : [])]);
            return;
        }

        const sourceDetail = /^\/sources\/([^/]+)$/.exec(path);
        if (sourceDetail) {
            const id = sourceDetail[1];
            const name = this.sourceDetailLabel.getSourceLabel();
            this.emit(path, [
                { label: 'Sources', url: '/sources' },
                { label: name ?? id, url: path }
            ]);
            return;
        }

        const tpDetail = /^\/treatment-protocols\/([^/]+)$/.exec(path);
        if (tpDetail) {
            const id = tpDetail[1];
            const name = this.tpDetailLabel.getLabel();
            this.emit(path, [
                { label: 'Treatment Protocols', url: '/treatment-protocols' },
                { label: name ?? id, url: path }
            ]);
            return;
        }

        const requestDetail = /^\/requests\/([^/]+)$/.exec(path);
        if (requestDetail) {
            const id = requestDetail[1];
            const name = this.requestDetailLabel.getRequestLabel();
            this.emit(path, [
                { label: 'Requests', url: '/requests' },
                { label: name ?? id, url: path }
            ]);
            return;
        }

        // Workflow screens opened from a request (e.g. Evidence Search via ?requestId=) get a request-aware
        // trail: Requests › REQ-N › <workflow>, where REQ-N links back to the request detail. The request id
        // comes from the URL query (so the return link works immediately); the human REQ-N number is filled
        // in from WorkflowRequestBreadcrumbService once the screen's request loads. This is the standardized
        // return path for all such screens — add the path here and have the screen feed the service.
        if (path === '/evidence-search') {
            const requestId = new URLSearchParams(this.router.url.split('?')[1] ?? '').get('requestId');
            const crumbs: Breadcrumb[] = [];
            if (requestId) {
                const ctx = this.workflowRequestLabel.get();
                const number = ctx?.requestId === requestId ? ctx.requestNumber : null;
                crumbs.push({ label: 'Requests', url: '/requests' });
                crumbs.push({ label: number != null ? `REQ-${number}` : 'Request', url: `/requests/${requestId}` });
            }
            crumbs.push({ label: 'Evidence Search' });
            this.emit(path, crumbs);
            return;
        }

        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumb(root, [], breadcrumbs);
        this.emit(path, breadcrumbs);
    }

    /** Every Administration page reads Administration › <tool> › …, linking back to the /admin hub. */
    private emit(path: string, crumbs: Breadcrumb[]): void {
        if (isAdminPath(path) && path !== '/admin') {
            crumbs = [{ label: 'Administration', url: '/admin' }, ...crumbs.filter((c) => c.label !== 'Administration')];
        }
        this._breadcrumbs$.next(crumbs);
    }

    private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
        const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
        const breadcrumb = route.data['breadcrumb'];
        const parentBreadcrumb = route.parent && route.parent.data ? route.parent.data['breadcrumb'] : null;

        if (breadcrumb && breadcrumb !== parentBreadcrumb) {
            breadcrumbs.push({
                label: route.data['breadcrumb'],
                url: '/' + routeUrl.join('/')
            });
        }

        if (route.firstChild) {
            this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
        }
    }

    logout(): void {
        this.auth.logout().subscribe({
            next: () => void this.router.navigate(['/login']),
            error: () => void this.router.navigate(['/login'])
        });
    }
}
